import { useCallback, useEffect, useRef, useState } from "react";
import { api, type ListerExisting, type ListerSessionSummary } from "../../lib/api";
import { createDecoder, acceptScan, decodeImageFile, type Decoder } from "../../lib/barcode";
import { digitsOnly, isValidGtin, looksLikeGtin, shouldRejectClientSide } from "../../lib/gtin";
import { hashFor } from "../../lib/routes";
import DuplicateModal from "./components/DuplicateModal";
import RecentSessions from "./components/RecentSessions";
import PendingBanner, { isPendingSession } from "./components/PendingBanner";

type Source = "camera" | "photo" | "manual" | "hid";
const FRAME_MS = 150;
// How far back to look for unfinished sessions when the scan page loads. Sessions come back
// newest-first, so this is a window, not a hard cap on what "pending" can mean — it is sized to
// comfortably hold everything a single shop scans in a good while without fetching its entire
// history just to show a nudge. The history page (reachable from here) is the source of truth
// for anything older than this window.
const PENDING_SCAN_WINDOW = 200;

// Native BarcodeDetector formats read like "upc_e" / "ean_8"; zxing-wasm formats
// read like "UPC-E" / "EAN-8". Either spelling maps to the same server hint.
export function hintFromFormat(format: string | undefined): "upc_e" | "ean_8" | undefined {
  if (!format) return undefined;
  if (/upc.?e/i.test(format)) return "upc_e";
  if (/ean.?8/i.test(format)) return "ean_8";
  return undefined;
}

export default function Scan() {
  const [decoder, setDecoder] = useState<Decoder | null>(null);
  const [camera, setCamera] = useState<"off" | "on" | "denied" | "insecure">("off");
  const [torch, setTorch] = useState<{ track: MediaStreamTrack; on: boolean } | null>(null);
  const [manual, setManual] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState<{ sessionId: string; existing: ListerExisting; result: { available: number | null; mode: "add" | "replace" } | null; quantity: number } | null>(null);
  const [recent, setRecent] = useState<ListerSessionSummary[]>([]);
  const [pending, setPending] = useState<ListerSessionSummary[]>([]);
  const [pendingDismissed, setPendingDismissed] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const qtyInputRef = useRef<HTMLInputElement>(null);
  const historyRef = useRef<string[]>([]);
  const frozenRef = useRef(false);
  // `submit` is a stable useCallback([]) closure, so `busy` state is stale inside it — a ref is
  // the only reliable single-flight guard against the manual-change handler, the Enter handler,
  // and the camera tick all racing to call submit() for the same scan.
  const busyRef = useRef(false);
  // The count the operator states BEFORE the barcode is read — the owner's actual problem is
  // that the pipeline takes minutes and the number is forgotten by the time it finishes. Kept
  // in a ref as well as state for the same reason busyRef exists: `submit` is a stable
  // useCallback([]) closure and the camera tick calls it autonomously, so plain state read
  // inside it would be stale and would send 1 on every camera scan.
  //
  // Starts empty, not 1: a silent default of 1 is indistinguishable from an operator who
  // actually meant one, and it kept shipping wrong stock whenever they forgot to type over it.
  // qty (string) is what the input displays; qtyRef mirrors the *parsed* value, or null when
  // the field is empty or not a valid 1-10000 integer — submit() below refuses to fire while
  // it is null, which is what makes the field required rather than merely labeled that way.
  const [qty, setQty] = useState("");
  const qtyRef = useRef<number | null>(null);

  // Only touch playback when a live stream is actually attached — no-op (not just harmless, but
  // silent) for the manual/photo-only flows where the video element was never started. real
  // browsers can also reject play() (AbortError etc.) — swallow either failure mode; this is
  // best-effort UX, not correctness.
  const safePause = useCallback((): void => {
    const v = videoRef.current;
    if (!v?.srcObject) return;
    try { v.pause(); } catch { /* not implemented in some test environments */ }
  }, []);
  const safePlay = useCallback((): void => {
    const v = videoRef.current;
    if (!v?.srcObject) return;
    try {
      const p = v.play();
      if (p && typeof p.then === "function") p.catch(() => {});
    } catch { /* not implemented in some test environments */ }
  }, []);

  const submit = useCallback(async (raw: string, source: Source, format?: string): Promise<void> => {
    if (busyRef.current) return;
    const gtin = digitsOnly(raw);
    const hint = hintFromFormat(format);
    if (shouldRejectClientSide(gtin, hint)) { setError("That code fails the GTIN check digit — rescan or retype it."); return; }
    const quantity = qtyRef.current;
    // Required, not just labeled required: a camera tick or an HID scanner typing into the
    // manual field can call submit() on its own, so the block has to live here, before
    // anything is sent, not just on a form's disabled-submit-button affordance.
    if (quantity == null) {
      setError("Type how many before scanning — quantity is required.");
      qtyInputRef.current?.focus();
      return;
    }
    busyRef.current = true; setBusy(true);
    setError(null); setNotice(null); frozenRef.current = true; safePause();
    try {
      if (navigator.vibrate) navigator.vibrate(30);
      const r = await api.createListerSession({ gtin, source, ...(hint ? { format: hint } : {}), quantity });
      if (r.existing) { setDup({ sessionId: r.id, existing: r.existing, result: null, quantity }); return; }
      if (r.dup_check === "failed") setNotice("Duplicate check unavailable — verify in Shopify before creating.");
      await api.startListerResearch(r.id);
      window.location.hash = hashFor.review(r.id);
    } catch (e) {
      setError(String(e)); frozenRef.current = false; safePlay();
    } finally { busyRef.current = false; setBusy(false); }
  }, [safePause, safePlay]);

  // decoder + camera
  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer = 0;
    (async () => {
      const d = await createDecoder();
      setDecoder(d);
      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia || d.kind === "none") { setCamera("insecure"); return; }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } }, audio: false });
      } catch { setCamera("denied"); return; }
      const v = videoRef.current; if (!v) return;
      v.srcObject = stream; await v.play(); setCamera("on");
      const track = stream.getVideoTracks()[0];
      const caps = track && "getCapabilities" in track ? (track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean }) : null;
      if (caps?.torch && track) setTorch({ track, on: false });
      const canvas = canvasRef.current!;
      const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
      const tick = async () => {
        try {
          if (!frozenRef.current && v.readyState >= 2) {
            const scale = Math.min(1, 1024 / v.videoWidth);
            canvas.width = Math.round(v.videoWidth * scale); canvas.height = Math.round(v.videoHeight * scale);
            ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
            const value = await d.decode(ctx.getImageData(0, 0, canvas.width, canvas.height)).catch(() => null);
            if (value && looksLikeGtin(value.text)) {
              const r = acceptScan(historyRef.current, digitsOnly(value.text));
              historyRef.current = r.history;
              if (r.accepted) { historyRef.current = []; void submit(value.text, "camera", value.format); }
            }
          }
        } catch {
          // a transient 0x0 canvas (video not yet sized) or a decode hiccup must not kill the loop
        } finally {
          timer = window.setTimeout(tick, FRAME_MS);
        }
      };
      timer = window.setTimeout(tick, FRAME_MS);
    })();
    // One fetch covers both the small "recent" strip and the pending-work nudge below — the
    // operator lands here to scan the next item, not to browse, so this stays a single request
    // rather than a second round trip just for the banner.
    api.listListerSessions(PENDING_SCAN_WINDOW).then((all) => {
      setRecent(all.slice(0, 5));
      setPending(all.filter(isPendingSession));
    }).catch(() => {});
    return () => { window.clearTimeout(timer); stream?.getTracks().forEach((t) => t.stop()); };
  }, [submit]);

  async function onPhoto(file: File | undefined): Promise<void> {
    if (!file || !decoder) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const local = await decodeImageFile(file, decoder);
      if (local) { await submit(local.text, "photo", local.format); return; }
      const b64 = await new Promise<string>((res, rej) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result).split(",")[1] ?? ""); fr.onerror = rej; fr.readAsDataURL(file); });
      const r = await api.decodeBarcodeImage(b64, file.type || "image/jpeg");
      const value = r.decoded ?? (r.agree ? r.vision : null);
      if (!value && r.vision) { setManual(r.vision); setError("Could not decode the bars — the printed digits read as below, check and press Enter."); return; }
      if (!value) { setError("No barcode found in that photo. Fill the frame with the barcode and try again."); return; }
      await submit(value, "photo");
    } catch (e) {
      setError(String(e));
    } finally { setBusy(false); }
  }

  function onManualChange(v: string): void {
    setManual(v); setError(null); setNotice(null);
    const d = digitsOnly(v);
    if ((d.length === 12 || d.length === 13) && isValidGtin(d)) void submit(d, "manual");   // HID scanners type into this field too; the source stays "manual"
  }

  const badge = camera === "on"
    ? (decoder?.kind === "native" ? "Native scanner" : "ZXing scanner")
    : camera === "denied"
    ? "Camera denied — photo or type"
    : decoder?.kind === "none" && window.isSecureContext
    ? "Camera unavailable — no barcode decoder in this browser"
    : "Photo only — HTTPS needed for live scanning";

  return (
    <div className="max-w-3xl mx-auto px-4 py-4 space-y-4 font-mono text-sm">
      <div className="flex items-center justify-between">
        <h1 className="text-zinc-100 text-base">Lister · scan</h1>
        <div className="flex items-center gap-3">
          <a href={hashFor.card()} className="text-xs text-zinc-400 underline">photograph a card</a>
          <span className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-zinc-400">{badge}</span>
        </div>
      </div>
      {!pendingDismissed && (
        <PendingBanner pending={pending} onDismiss={() => setPendingDismissed(true)} />
      )}
      <div className="space-y-1">
        <label className="flex items-center gap-3">
          <span className="text-zinc-300">Quantity to add <span className="text-amber-400">*</span></span>
          <input ref={qtyInputRef} aria-label="Quantity to add" aria-required="true" required type="number" inputMode="numeric" min={1} max={10000}
            placeholder="required" value={qty}
            onChange={(e) => {
              const v = e.target.value;
              setQty(v);
              const n = v === "" ? NaN : Number(v);
              qtyRef.current = Number.isInteger(n) && n >= 1 && n <= 10000 ? n : null;
            }}
            className="w-24 bg-zinc-950 border border-zinc-800 rounded px-3 py-3 text-center placeholder:text-zinc-600" />
        </label>
        <p className="text-xs text-zinc-500">required — type this before you scan, it becomes the listing's quantity, or the amount added to stock</p>
      </div>
      <div className="relative bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden aspect-[4/3] md:aspect-video">
        <video ref={videoRef} className={`w-full h-full object-cover ${camera === "on" ? "" : "hidden"}`} muted playsInline />
        <canvas ref={canvasRef} className="hidden" />
        {camera !== "on" && <div className="absolute inset-0 flex items-center justify-center text-zinc-500 px-6 text-center">Use the photo button or type the barcode.</div>}
        {camera === "on" && <div className="absolute inset-x-8 top-1/2 h-24 -translate-y-1/2 border-2 border-green-400/60 rounded" />}
        {torch && camera === "on" && (
          <button className="absolute top-2 right-2 bg-zinc-800/80 px-3 py-2 rounded" onClick={async () => {
            try {
              await torch.track.applyConstraints({ advanced: [{ torch: !torch.on } as MediaTrackConstraintSet] });
              setTorch({ ...torch, on: !torch.on });
            } catch (e) { setError(String(e)); }
          }}>
            {torch.on ? "torch off" : "torch"}
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="bg-zinc-800 hover:bg-zinc-700 rounded px-3 py-3 text-center cursor-pointer">
          {busy ? "working…" : "Take a photo"}
          <input type="file" accept="image/*" capture="environment" className="hidden" disabled={busy} onChange={(e) => {
            const input = e.target;
            void onPhoto(input.files?.[0]).finally(() => { input.value = ""; }); // reset so retaking the same photo still fires a change event
          }} />
        </label>
        <label className="block">
          <span className="sr-only">Barcode</span>
          <input aria-label="Barcode" inputMode="numeric" autoComplete="off" placeholder="type or scan a barcode" value={manual} disabled={busy}
            onChange={(e) => onManualChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void submit(manual, "manual"); }}
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-3 focus:outline-none focus:border-zinc-600" />
        </label>
      </div>
      {notice && <p className="text-zinc-500" role="status">{notice}</p>}
      {error && <p className="text-red-400" role="alert">{error}</p>}
      <RecentSessions sessions={recent} />
      {dup && (
        <DuplicateModal sessionId={dup.sessionId} existing={dup.existing} initial={dup.quantity} result={dup.result}
          onRestock={async (delta, mode) => {
            try {
              const r = await api.restockListerSession(dup.sessionId, delta, mode, mode === "replace");
              setDup({ ...dup, result: { available: r.available, mode: r.mode } });
            } catch (e) { setError(String(e)); }
          }}
          onCreateAnyway={async () => {
            try {
              await api.startListerResearch(dup.sessionId);
              window.location.hash = hashFor.review(dup.sessionId);
            } catch (e) { setError(String(e)); }
          }}
          onClose={() => {
            setDup(null); frozenRef.current = false; safePlay(); setManual("");
            // The next scan is a different item and must be stated fresh too — carrying this
            // one's quantity forward silently is the same bug the empty default was fixing.
            setQty(""); qtyRef.current = null;
          }} />
      )}
    </div>
  );
}
